import { useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { NAV_LINKS } from "@/lib/constants";
import { toast, Toaster } from "sonner";
import ShareButton from "@/components/ShareButton";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await http.post("/newsletter", { email });
      toast.success("Subscribed. The archive will find you.");
      setEmail("");
    } catch { toast.error("Something went wrong."); }
    finally { setBusy(false); }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-black/10 pt-24 pb-10 bg-[color:var(--np-bg)]">
      <Toaster theme="dark" position="bottom-right" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Living archive since 2025</div>
            <h3 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-[color:var(--np-text)]">
              Every place on Earth<br/>has a story.
            </h3>
            <p className="mt-6 max-w-md text-[color:var(--np-text-2)]">
              NaturePulse combines authoritative climate science with community observations, photography, and daily environmental updates into a permanent record of our changing planet.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md">
              <input
                data-testid="newsletter-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                placeholder="your@email.earth"
                className="flex-1 bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-3 font-mono-tech text-sm tracking-wide"
              />
              <button
                data-testid="newsletter-submit-btn"
                disabled={busy}
                className="font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/15 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-5 py-3 transition-colors"
              >{busy ? "Sending…" : "Subscribe"}</button>
            </form>
          </div>

          <div className="md:col-span-4 md:col-start-8 grid grid-cols-2 gap-6">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mb-4">Sections</div>
              <ul className="space-y-2">
                {NAV_LINKS.slice(0,5).map(l => (
                  <li key={l.label}><Link to={l.to} className="font-serif-display text-lg hover:text-[color:var(--np-brand)]">{l.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mb-4">Network</div>
              <ul className="space-y-2">
                {NAV_LINKS.slice(5).map(l => (
                  <li key={l.label}>
                    {l.external ? (
                      <a href={l.to} target="_blank" rel="noreferrer" className="font-serif-display text-lg hover:text-[color:var(--np-brand)]">{l.label} <span className="text-xs align-middle text-[color:var(--np-muted)]">↗</span></a>
                    ) : (
                      <Link to={l.to} className="font-serif-display text-lg hover:text-[color:var(--np-brand)]">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-24 overflow-hidden">
          <div className="font-serif-display font-light leading-none tracking-tighter text-[color:var(--np-text)] text-[18vw] whitespace-nowrap select-none">
            NaturePulse
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[color:var(--np-muted)] font-mono-tech tracking-wider uppercase">
          <div>© {new Date().getFullYear()} NaturePulse · A Social Pulse Endeavor</div>
          <div className="flex items-center gap-4 flex-wrap">
            <ShareButton
              title="NaturePulse"
              text="A living diary of the planet, created by all of us."
              hashtags={["NaturePulse", "Nature", "Climate"]}
              variant="pill"
              size="sm"
              testid="footer-share"
            />
            <span>Data: NOAA · NASA · IPCC · WMO · IUCN</span>
            <Link to="/contact" className="hover:text-[color:var(--np-text)]">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
